import React, { useCallback, useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import TextField from '../../atoms/TextField/TextField'
import SearchIcon from '../../atoms/Svgs/SearchOff'
import AddIcon from '../../atoms/Icons/Controls/Plus'
import CloseIcon from '../../atoms/Svgs/Close'
import Search, { SearchPropTypes } from '../../molecules/Search/Search'
import styles from './SearchInputOverlay.module.scss'
import TextInput from 'views/components/atoms/TextField/TextInput'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'

interface SearchInputOverlayProps {
  onChange?: (value: string) => void
  name?: string
  placeholder?: string
  iconFill?: string
  icon?: 'search' | 'add'
  className?: string
  useSearchComponent?: boolean
  searchComponentProps?: SearchPropTypes
  children?: React.ReactNode
}

const SearchInputOverlay = ({
  onChange,
  name = 'search',
  placeholder = 'Search...',
  iconFill = '#141719',
  icon = 'search',
  className,
  useSearchComponent,
  searchComponentProps = {},
  children,
}: SearchInputOverlayProps): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  const toggle = useCallback(() => {
    if (isOpen) {
      setIsOpen(false)
      setQuery('')
    } else {
      setIsOpen(true)
    }
  }, [isOpen])

  const classes = cx([styles.root, className], {
    [styles['is-open']]: isOpen,
  })

  useEffect(() => {
    if (isOpen && !useSearchComponent) {
      const timer = setTimeout(() => {
        inputRef.current?.focus()
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [isOpen, useSearchComponent])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value
      setQuery(val)
      onChange?.(val)
    },
    [onChange],
  )

  const Icon = icon === 'search' ? SearchIcon : AddIcon

  return (
    <div className={classes}>
      <div className={styles.children}>{children}</div>
      {useSearchComponent ? (
        <Search
          name={name}
          placeholder={placeholder}
          value={query}
          className={styles['search-wrapper']}
          {...searchComponentProps}
        />
      ) : (
        <TextField
          inputComponent={
            <TextInput
              type={TextFieldTypes.text}
              name={name}
              placeholder={placeholder}
              onChange={handleChange}
              value={query}
              ref={(ref: HTMLInputElement | null) => {
                inputRef.current = ref
              }}
            />
          }
        />
      )}
      <button type="button" className={styles['open-button']} onClick={toggle}>
        {isOpen ? <CloseIcon fill={iconFill} /> : <Icon fill={iconFill} />}
      </button>
    </div>
  )
}

export default SearchInputOverlay
